import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserPlus, Trash2, CheckCircle, XCircle } from 'lucide-react';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'admin',
    active: true,
    receiveNotifications: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'authorized_users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const emailLower = newUser.email.toLowerCase().trim();
      
      // Check if user already exists
      const userDocRef = doc(db, 'authorized_users', emailLower);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        alert('Este correo electrónico ya está registrado como usuario.');
        return;
      }

      await setDoc(userDocRef, {
        email: emailLower,
        name: newUser.name,
        role: newUser.role,
        active: newUser.active,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      setNewUser({ email: '', name: '', role: 'admin', active: true });
      setShowAddForm(false);
      loadUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      alert('Error al agregar usuario');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'authorized_users', userId), {
        active: !currentStatus,
        updatedAt: new Date()
      });
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const toggleUserNotifications = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'authorized_users', userId), {
        receiveNotifications: !currentStatus,
        updatedAt: new Date()
      });
      loadUsers();
    } catch (error) {
      console.error('Error updating user notifications:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await deleteDoc(doc(db, 'authorized_users', userId));
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container user-management-container">
      <div className="page-header">
        <h1>Gestión de Usuarios Autorizados</h1>
        <button 
          onClick={() => setShowAddForm(!showAddForm)} 
          className="btn btn-primary"
        >
          <UserPlus size={20} />
          Agregar Usuario
        </button>
      </div>

      {showAddForm && (
        <div className="card add-user-form">
          <h3>Nuevo Usuario Autorizado</h3>
          <form onSubmit={handleAddUser}>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                placeholder="usuario@desertpointtech.com"
              />
            </div>
            <div className="form-group">
              <label>Nombre *</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
                placeholder="Nombre completo"
              />
            </div>
            <div className="form-group">
              <label>Rol</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="admin">Administrador</option>
                <option value="viewer">Visualizador</option>
              </select>
            </div>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newUser.receiveNotifications}
                  onChange={(e) => setNewUser({ ...newUser, receiveNotifications: e.target.checked })}
                />
                Recibir notificaciones por correo de nuevas denuncias
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Agregar Usuario
              </button>
              <button 
                type="button" 
                onClick={() => setShowAddForm(false)} 
                className="btn btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card users-list">
        <h3>Usuarios Autorizados ({users.length})</h3>
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Nombre</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Notificaciones</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.name}</td>
                <td>
                  <span className="role-badge">{user.role}</span>
                </td>
                <td>
                  {user.active ? (
                    <span className="status-badge status-active">
                      <CheckCircle size={16} />
                      Activo
                    </span>
                  ) : (
                    <span className="status-badge status-inactive">
                      <XCircle size={16} />
                      Inactivo
                    </span>
                  )}
                </td>
                <td>
                  {user.receiveNotifications ? (
                    <span className="status-badge status-active">
                      <CheckCircle size={16} />
                      Sí
                    </span>
                  ) : (
                    <span className="status-badge status-inactive">
                      <XCircle size={16} />
                      No
                    </span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => toggleUserNotifications(user.id, user.receiveNotifications)}
                      className="btn-action"
                      title={user.receiveNotifications ? 'Desactivar Notificaciones' : 'Activar Notificaciones'}
                    >
                      {user.receiveNotifications ? <XCircle size={18} /> : <CheckCircle size={18} />}
                    </button>
                    <button
                      onClick={() => toggleUserStatus(user.id, user.active)}
                      className="btn-action"
                      title={user.active ? 'Desactivar Usuario' : 'Activar Usuario'}
                    >
                      {user.active ? <XCircle size={18} /> : <CheckCircle size={18} />}
                    </button>
                    <button
                      onClick={() => deleteUser(user.id)}
                      className="btn-action btn-danger"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;
